import React, { useState } from 'react';
import { Check, Heart, Gamepad2, Music, Mail } from 'lucide-react';
import { SiDiscord, SiLinkedin, SiGmail } from 'react-icons/si';

const contactData = [
    {
        key: 'email',
        icon: SiGmail,
        color: 'hover:bg-white/8 hover:text-white hover:border-white/20',
        activeColor: 'bg-white/8 text-white border-white/20'
    },
    {
        key: 'discord',
        icon: SiDiscord,
        color: 'hover:bg-white/8 hover:text-white hover:border-white/20',
        activeColor: 'bg-white/8 text-white border-white/20'
    },
    {
        key: 'linkedin',
        icon: SiLinkedin,
        color: 'hover:bg-white/8 hover:text-white hover:border-white/20',
        activeColor: 'bg-white/8 text-white border-white/20'
    },
    {
        key: 'genshinUid',
        icon: Gamepad2,
        color: 'hover:bg-white/8 hover:text-white hover:border-white/20',
        activeColor: 'bg-white/8 text-white border-white/20'
    },
    {
        key: 'projectSekaiId',
        icon: Music,
        color: 'hover:bg-white/8 hover:text-white hover:border-white/20',
        activeColor: 'bg-white/8 text-white border-white/20'
    }
];

const ContactSection = ({ contacts }) => {
    const [copiedStates, setCopiedStates] = useState({});
    const [hoveredKey, setHoveredKey] = useState(null);

    const handleCopy = async (key, value) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopiedStates({ ...copiedStates, [key]: true });
            setTimeout(() => {
                setCopiedStates(prev => ({ ...prev, [key]: false }));
            }, 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    const getLabel = (key) => {
        const labels = {
            email: "Email",
            discord: "Discord",
            linkedin: "LinkedIn",
            genshinUid: "Genshin UID",
            projectSekaiId: "Project Sekai"
        };
        return labels[key] || key;
    };

    return (
        <section id="contact" className="py-20 md:py-32 relative">
            <div className="max-w-4xl mx-auto px-4 md:px-8">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <h2 className="text-4xl md:text-6xl font-display mb-4 text-white tracking-wide">
                        Let's Connect
                    </h2>
                    <p className="text-white/40 max-w-2xl mx-auto text-sm font-mono">
                        Click any icon to copy
                    </p>
                </div>

                {/* Contact Icons */}
                <div className="flex flex-wrap justify-center gap-4">
                    {contactData.map(({ key, icon: Icon, color, activeColor }) => {
                        const value = contacts[key];
                        if (!value) return null;

                        const isCopied = copiedStates[key];
                        const isHovered = hoveredKey === key;

                        return (
                            <div key={key} className="relative">
                                <button
                                    onClick={() => handleCopy(key, value)}
                                    onMouseEnter={() => setHoveredKey(key)}
                                    onMouseLeave={() => setHoveredKey(null)}
                                    className={`
                                        w-12 h-12 md:w-14 md:h-14 rounded-xl
                                        flex items-center justify-center
                                        bg-white/[0.03] border border-white/8
                                        text-white/40
                                        transition-all duration-300
                                        hover:scale-105
                                        ${isCopied ? activeColor : color}
                                    `}
                                >
                                    {isCopied ? (
                                        <Check className="w-5 h-5" />
                                    ) : (
                                        <Icon className="w-5 h-5" />
                                    )}
                                </button>

                                {/* Tooltip */}
                                <div className={`
                                    absolute -bottom-14 left-1/2 -translate-x-1/2
                                    px-3 py-2 rounded-lg
                                    bg-cute-dark border border-white/10
                                    text-xs text-center whitespace-nowrap
                                    transition-all duration-200
                                    ${isHovered || isCopied ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1 pointer-events-none'}
                                `}>
                                    <p className="text-white/30 text-[10px] uppercase tracking-wider">{getLabel(key)}</p>
                                    <p className="text-white/70 font-medium mt-0.5">{isCopied ? 'Copied!' : value}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Email button */}
                <div className="mt-16 text-center">
                    <a
                        href="mailto:estherdjaidiguna@gmail.com"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black text-sm font-medium hover:bg-white/90 transition-all duration-300 hover:scale-105"
                    >
                        <Mail className="w-4 h-4" />
                        Send me an email
                    </a>
                </div>

                {/* Footer */}
                <div className="mt-20 text-center">
                    <p className="text-white/20 text-sm flex items-center justify-center gap-1">
                        Thanks for visiting <Heart className="w-4 h-4 text-white/30 fill-white/20" />
                    </p>
                </div>
            </div>
        </section>
    );
};

export default ContactSection;
